import type { FormField } from '@shared/components/add-form/add-form.component';
import type { OfferSchemaApiField } from '@app/api/promotion-service/model/offerSchemaApiField';

export const OFFER_CONFIGURATION_SECTION = 'offer.form.sections.configuration';
const TENDER_TYPE_FIELD = 'tenderTypeCode';
const TENDER_TYPE_LABELS: Record<string, { label: string; labelVi: string }> = {
  CASH: { label: 'Cash', labelVi: 'Tiền mặt' },
  CARD: { label: 'Card', labelVi: 'Thẻ' },
  E_WALLET: { label: 'E-Wallet', labelVi: 'Ví điện tử' },
};

/** Client row model for schema-driven array tables (API rows + stable id). */
export interface SchemaArrayRow {
  id: string;
  [key: string]: unknown;
}

/** Prefix definition JSON fields to avoid clashing with offer form (e.g. description). */
export const DEF_FIELD_PREFIX = 'def__';

export function encodeSchemaFieldName(apiName: string): string {
  return apiName.replace(/\./g, '__dot__');
}

export function decodeSchemaFieldName(controlName: string): string {
  return controlName.replace(/__dot__/g, '.');
}

export function definitionControlName(apiName: string): string {
  return `${DEF_FIELD_PREFIX}${encodeSchemaFieldName(apiName)}`;
}

export function setNested(
  obj: Record<string, unknown>,
  path: string[],
  value: unknown,
): void {
  if (path.length === 0) return;
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    if (cur[k] === undefined || typeof cur[k] !== 'object') {
      cur[k] = {};
    }
    cur = cur[k] as Record<string, unknown>;
  }
  cur[path[path.length - 1]] = value;
}

export function getNested(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const k of path) {
    if (cur === null || cur === undefined || typeof cur !== 'object') {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[k];
  }
  return cur;
}

type ApiFieldExt = OfferSchemaApiField & {
  labelVi?: string;
  enumValueLabelsVi?: Record<string, string>;
  /** Served by the schema API; conditional (requiredWhen/requireOneOf) rules stay server-enforced. */
  required?: boolean;
};

export function jsonTypeOf(f: OfferSchemaApiField): string {
  return (f.jsonType ?? '').toUpperCase();
}

/** Schema API has no numeric bound yet, so infer one from the field name until it does. */
export function inferredBoundsOf(name: string): { min?: number; max?: number } {
  return name.includes('Percent') ? { min: 0, max: 100 } : {};
}

export function getArrayItemProperties(
  f: OfferSchemaApiField,
): OfferSchemaApiField[] | null {
  const items = f.items as OfferSchemaApiField | undefined;
  if (!items || typeof items !== 'object') return null;
  const props = (items as OfferSchemaApiField & {
    properties?: OfferSchemaApiField[];
  }).properties;
  return Array.isArray(props) && props.length > 0 ? props : null;
}

export interface SchemaArrayFieldDef {
  name: string;
  label: string;
  labelVi?: string;
  itemProperties: OfferSchemaApiField[];
}

export function listSchemaArrayFields(
  fields: OfferSchemaApiField[] | undefined,
): SchemaArrayFieldDef[] {
  const out: SchemaArrayFieldDef[] = [];
  for (const f of fields ?? []) {
    if (jsonTypeOf(f) !== 'ARRAY' || !f.name) continue;
    const itemProps = getArrayItemProperties(f);
    if (!itemProps) continue;
    const ext = f as ApiFieldExt;
    out.push({
      name: f.name,
      label: f.label ?? f.name,
      labelVi: ext.labelVi,
      itemProperties: itemProps,
    });
  }
  return out;
}

export function newRowId(): string {
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyRowFromProperties(
  props: OfferSchemaApiField[],
): SchemaArrayRow {
  const row = { id: newRowId() } as SchemaArrayRow;
  for (const p of props) {
    const n = p.name;
    if (!n) continue;
    const jt = jsonTypeOf(p);
    if (jt === 'BOOLEAN' || jt === 'BOOL') {
      row[n] = false;
    } else {
      row[n] = null;
    }
  }
  return row;
}

export function rowsFromApiArray(
  raw: unknown,
  props: OfferSchemaApiField[],
): SchemaArrayRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const row = emptyRowFromProperties(props);
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      for (const p of props) {
        const n = p.name;
        if (n && o[n] !== undefined) {
          row[n] = o[n];
        }
      }
    }
    row['id'] = newRowId();
    return row;
  });
}

export function serializeRowsForApi(rows: SchemaArrayRow[]): unknown[] {
  return rows.map((row) => {
    const copy: Record<string, unknown> = { ...row };
    delete copy['id'];
    return copy;
  });
}

export function mapOfferSchemaFieldToFormField(
  f: OfferSchemaApiField,
): FormField | null {
  const name = f.name?.trim();
  if (!name) return null;

  const jt = jsonTypeOf(f);
  if (jt === 'ARRAY' || jt === 'OBJECT') return null;

  const ext = f as ApiFieldExt;
  const labelEn = f.label ?? name;
  const labelVi = ext.labelVi;
  const controlName = definitionControlName(name);
  const base: Partial<FormField> = {
    controlName,
    section: OFFER_CONFIGURATION_SECTION,
    hidden: false,
    translateLabel: false,
    label: labelEn,
    labelVi,
  };

  if (jt === 'ENUM' || (f.enumValues?.length ?? 0) > 0) {
    const opts = (f.enumValues ?? []).map((ev) => ({
      label: optionLabelFor(f, ev),
      value: ev,
      labelVi: optionLabelViFor(f, ev),
    }));
    return {
      ...base,
      type: 'dropdown',
      placeholder: labelEn,
      placeholderVi: labelVi ?? labelEn,
      options: opts,
      required: !!ext.required,
    } as FormField;
  }

  if (jt === 'BOOLEAN' || jt === 'BOOL') {
    const boolField: FormField = {
      ...base,
      type: 'dropdown',
      placeholder: labelEn,
      placeholderVi: labelVi ?? labelEn,
      options: [
        { label: 'Yes', value: true, labelVi: 'Có' },
        { label: 'No', value: false, labelVi: 'Không' },
      ],
      required: !!ext.required,
    } as FormField;
    return boolField;
  }

  if (
    jt === 'NUMBER' ||
    jt === 'INTEGER' ||
    jt === 'INT' ||
    jt === 'LONG' ||
    jt === 'DOUBLE' ||
    jt === 'FLOAT' ||
    jt === 'BIGDECIMAL'
  ) {
    return {
      ...base,
      type: 'number',
      placeholder: labelEn,
      placeholderVi: labelVi ?? labelEn,
      required: !!ext.required,
      ...inferredBoundsOf(name),
    } as FormField;
  }

  return {
    ...base,
    type: 'text',
    placeholder: labelEn,
    placeholderVi: labelVi ?? labelEn,
    maxLength: 500,
    required: !!ext.required,
  } as FormField;
}

export function optionLabelFor(f: OfferSchemaApiField, value: string): string {
  if (f.name === TENDER_TYPE_FIELD && TENDER_TYPE_LABELS[value]) {
    return TENDER_TYPE_LABELS[value].label;
  }
  return f.enumValueLabels?.[value] ?? value;
}

export function optionLabelViFor(
  f: OfferSchemaApiField,
  value: string,
): string | undefined {
  if (f.name === TENDER_TYPE_FIELD && TENDER_TYPE_LABELS[value]) {
    return TENDER_TYPE_LABELS[value].labelVi;
  }
  const ext = f as ApiFieldExt;
  return ext.enumValueLabelsVi?.[value];
}

export function flattenDefinitionForForm(
  parsed: Record<string, unknown> | null,
  fields: OfferSchemaApiField[],
): Record<string, unknown> {
  if (!parsed) return {};
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const jt = jsonTypeOf(f);
    if (jt === 'ARRAY' || !f.name) continue;
    const path = f.name.split('.');
    const v = getNested(parsed, path);
    if (v !== undefined) {
      out[definitionControlName(f.name)] = v;
    }
  }
  return out;
}
